/**
 * Communication Center Hub — The Operational Memory of Aligned
 * 
 * Philosophy: Communication is work, not chat.
 * Every communication links to entities (Client, Visit, Care Plan, etc.)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, Send, Mic, Phone, AlertTriangle, CheckCircle2,
  Clock, User, Users, FileText, Search, Filter, Plus, X,
  ChevronRight, Bell, Calendar, Clipboard, Sparkles, Volume2,
  RefreshCw, Archive, Pin, MoreVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

// Communication type icons and colors
const TYPE_CONFIG = {
  message: { icon: MessageSquare, color: 'bg-blue-100 text-blue-700', label: 'Message' },
  task: { icon: Clipboard, color: 'bg-purple-100 text-purple-700', label: 'Task' },
  notification: { icon: Bell, color: 'bg-yellow-100 text-yellow-700', label: 'Notification' },
  approval: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Approval' },
  announcement: { icon: Users, color: 'bg-indigo-100 text-indigo-700', label: 'Announcement' },
  emergency: { icon: AlertTriangle, color: 'bg-red-100 text-red-700', label: 'Emergency' },
  voice_message: { icon: Mic, color: 'bg-teal-100 text-teal-700', label: 'Voice' },
  system: { icon: RefreshCw, color: 'bg-gray-100 text-gray-700', label: 'System' },
};

// Priority colors
const PRIORITY_CONFIG = {
  low: { color: 'bg-gray-100 text-gray-600', label: 'Low' },
  normal: { color: 'bg-blue-100 text-blue-600', label: 'Normal' },
  high: { color: 'bg-orange-100 text-orange-600', label: 'High' },
  urgent: { color: 'bg-red-100 text-red-600', label: 'Urgent' },
  emergency: { color: 'bg-red-500 text-white', label: 'Emergency' },
};

export default function CommunicationHub() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // State
  const [communications, setCommunications] = useState([]);
  const [threads, setThreads] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [activeTab, setActiveTab] = useState('all'); // all, tasks, approvals, threads
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  
  // Compose modal
  const [showCompose, setShowCompose] = useState(false);
  const [composeType, setComposeType] = useState('message');
  
  // Entity context from URL params
  const entityType = searchParams.get('entity_type');
  const entityId = searchParams.get('entity_id');

  // Fetch communications
  const fetchCommunications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter) params.append('type', typeFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (entityType) params.append('entity_type', entityType);
      if (entityId) params.append('entity_id', entityId);
      params.append('limit', '50');
      
      const [commsRes, tasksRes, approvalsRes, threadsRes] = await Promise.all([
        api.get(`/communications?${params.toString()}`),
        api.get('/communications/tasks/my'),
        api.get('/communications/approvals/pending'),
        api.get('/communications/threads'),
      ]);
      
      setCommunications(commsRes.data.communications || []);
      setMyTasks(tasksRes.data.tasks || []);
      setPendingApprovals(approvalsRes.data.approvals || []);
      setThreads(threadsRes.data.threads || []);
      
    } catch (err) {
      console.error('Failed to fetch communications:', err);
      setError('Failed to load communications');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter, priorityFilter, entityType, entityId]);
  
  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);
  
  // Get items for current tab
  const getTabItems = () => {
    switch (activeTab) {
      case 'tasks':
        return myTasks;
      case 'approvals':
        return pendingApprovals;
      case 'threads':
        return threads;
      default:
        return communications;
    }
  };
  
  // Stats
  const stats = {
    unread: communications.filter(c => 
      c.recipients?.some(r => r.user_id === user?.id && !r.read_at)
    ).length,
    tasks: myTasks.filter(t => t.task?.status === 'pending').length,
    approvals: pendingApprovals.length,
    urgent: communications.filter(c => c.priority === 'urgent' || c.priority === 'emergency').length,
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="communication-hub">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[var(--color-primary)]/10 rounded-lg">
                <MessageSquare className="w-6 h-6 text-[var(--color-primary)]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Communication Center</h1>
                <p className="text-sm text-gray-500">Operational memory</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowCompose(true)}
                className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
                data-testid="compose-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Communication
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-6">
            <StatBadge
              icon={MessageSquare}
              label="Unread"
              count={stats.unread}
              color="blue"
            />
            <StatBadge
              icon={Clipboard}
              label="My Tasks"
              count={stats.tasks}
              color="purple"
              onClick={() => setActiveTab('tasks')}
            />
            <StatBadge
              icon={CheckCircle2}
              label="Pending Approval"
              count={stats.approvals}
              color="green"
              onClick={() => setActiveTab('approvals')}
            />
            {stats.urgent > 0 && (
              <StatBadge
                icon={AlertTriangle}
                label="Urgent"
                count={stats.urgent}
                color="red"
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Entity Context Banner */}
      {entityType && entityId && (
        <div className="bg-blue-50 border-b border-blue-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <FileText className="w-4 h-4" />
                <span>Viewing communications for: <strong className="capitalize">{entityType}</strong></span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/communications')}
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filter
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Left Panel - Filters & Tabs */}
          <div className="w-64 flex-shrink-0">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search communications..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            {/* Tabs */}
            <nav className="space-y-1 mb-6">
              {[
                { id: 'all', label: 'All Communications', icon: MessageSquare },
                { id: 'tasks', label: 'My Tasks', icon: Clipboard, badge: stats.tasks },
                { id: 'approvals', label: 'Pending Approvals', icon: CheckCircle2, badge: stats.approvals },
                { id: 'threads', label: 'Threads', icon: Users },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <div className="flex items-center gap-2">
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </div>
                  {tab.badge > 0 && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-primary)] text-white">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
            
            {/* Type Filter */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                {Object.entries(TYPE_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            
            {/* Priority Filter */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">
                Priority
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="priority-filter"
              >
                <option value="">All Priorities</option>
                {Object.entries(PRIORITY_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Right Panel - Communications List */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-gray-600">{error}</p>
                <Button variant="outline" onClick={fetchCommunications} className="mt-4">
                  Retry
                </Button>
              </div>
            ) : getTabItems().length === 0 ? (
              <EmptyState tab={activeTab} onCompose={() => setShowCompose(true)} />
            ) : activeTab === 'threads' ? (
              <ThreadList threads={threads} onSelect={(t) => navigate(`/communications/thread/${t.id}`)} />
            ) : (
              <CommunicationList 
                items={getTabItems()} 
                onSelect={(c) => navigate(`/communications/${c.id}`)}
                isTaskView={activeTab === 'tasks'}
                isApprovalView={activeTab === 'approvals'}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Compose Modal */}
      {showCompose && (
        <ComposeModal
          type={composeType}
          setType={setComposeType}
          entityType={entityType}
          entityId={entityId}
          onClose={() => setShowCompose(false)}
          onSuccess={() => {
            setShowCompose(false);
            fetchCommunications();
          }}
        />
      )}
    </div>
  );
}

// Stat Badge Component
function StatBadge({ icon: Icon, label, count, color, onClick }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${colors[color]} transition-opacity hover:opacity-80`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
      <span className="text-sm font-semibold">{count}</span>
    </button>
  );
}

// Empty State Component
function EmptyState({ tab, onCompose }) {
  const messages = {
    all: { title: 'No communications yet', desc: 'Start by sending a message or creating a task' },
    tasks: { title: 'No tasks assigned', desc: 'Tasks assigned to you will appear here' },
    approvals: { title: 'No pending approvals', desc: 'Approval requests will appear here' },
    threads: { title: 'No conversation threads', desc: 'Start a conversation to create a thread' },
  };
  
  const { title, desc } = messages[tab] || messages.all;
  
  return (
    <div className="text-center py-12" data-testid="empty-state">
      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-500 mb-4">{desc}</p>
      <Button onClick={onCompose}>
        <Plus className="w-4 h-4 mr-2" />
        New Communication
      </Button>
    </div>
  );
}

// Communication List Component
function CommunicationList({ items, onSelect, isTaskView, isApprovalView }) {
  return (
    <div className="space-y-2" data-testid="communication-list">
      {items.map((item) => (
        <CommunicationCard
          key={item.id}
          item={item}
          onSelect={onSelect}
          isTaskView={isTaskView}
          isApprovalView={isApprovalView}
        />
      ))}
    </div>
  );
}

// Communication Card Component
function CommunicationCard({ item, onSelect, isTaskView, isApprovalView }) {
  const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.message;
  const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.normal;
  const TypeIcon = typeConfig.icon;
  
  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div
      onClick={() => onSelect(item)}
      className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
      data-testid={`communication-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div className={`p-2 rounded-lg ${typeConfig.color}`}>
          <TypeIcon className="w-4 h-4" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              {item.subject ? (
                <h3 className="font-medium text-gray-900 truncate">{item.subject}</h3>
              ) : (
                <h3 className="font-medium text-gray-900 truncate">
                  {item.content?.substring(0, 50)}{item.content?.length > 50 ? '...' : ''}
                </h3>
              )}
              {item.is_pinned && <Pin className="w-3 h-3 text-gray-400" />}
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {formatTime(item.created_at)}
            </span>
          </div>
          
          {/* Sender */}
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
            <User className="w-3 h-3" />
            <span>{item.sender_name}</span>
            {item.sender_role && (
              <span className="text-gray-400">• {item.sender_role}</span>
            )}
          </div>
          
          {/* Content Preview */}
          <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>
          
          {/* Tags and Context */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {/* Priority */}
            {item.priority !== 'normal' && (
              <Badge className={priorityConfig.color}>
                {priorityConfig.label}
              </Badge>
            )}
            
            {/* Task Status */}
            {isTaskView && item.task && (
              <Badge className="bg-purple-100 text-purple-700">
                {item.task.status}
              </Badge>
            )}
            
            {/* Approval Status */}
            {isApprovalView && item.approval && (
              <Badge className="bg-green-100 text-green-700">
                {item.approval.status}
              </Badge>
            )}
            
            {/* Voice indicator */}
            {item.voice_message && (
              <Badge className="bg-teal-100 text-teal-700">
                <Mic className="w-3 h-3 mr-1" />
                Voice
              </Badge>
            )}
            
            {/* AI Suggestions */}
            {item.ai_suggestions?.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700">
                <Sparkles className="w-3 h-3 mr-1" />
                {item.ai_suggestions.length} suggestion{item.ai_suggestions.length !== 1 ? 's' : ''}
              </Badge>
            )}
            
            {/* Contexts */}
            {item.contexts?.slice(0, 2).map((ctx, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {ctx.entity_type}: {ctx.entity_name || ctx.entity_id.substring(0, 8)}
              </Badge>
            ))}
            {item.contexts?.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{item.contexts.length - 2} more
              </Badge>
            )}
          </div>
        </div>
        
        <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
      </div>
    </div>
  );
}

// Thread List Component
function ThreadList({ threads, onSelect }) {
  return (
    <div className="space-y-2" data-testid="thread-list">
      {threads.map((thread) => (
        <div
          key={thread.id}
          onClick={() => onSelect(thread)}
          className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-gray-900">{thread.subject}</h3>
            <span className="text-xs text-gray-500">
              {thread.message_count} message{thread.message_count !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            <span>{thread.participant_names?.slice(0, 3).join(', ')}</span>
            {thread.participant_names?.length > 3 && (
              <span>+{thread.participant_names.length - 3} more</span>
            )}
          </div>
          {thread.last_message_preview && (
            <p className="text-sm text-gray-600 mt-2 truncate">{thread.last_message_preview}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// Compose Modal Component
function ComposeModal({ type, setType, entityType, entityId, onClose, onSuccess }) {
  const [content, setContent] = useState('');
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('normal');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    try {
      setSending(true);
      setError(null);
      
      const payload = {
        type,
        priority,
        content: content.trim(),
        request_ai_suggestions: true,
      };
      
      if (subject.trim()) {
        payload.subject = subject.trim();
      }
      
      if (entityType && entityId) {
        payload.primary_context = {
          entity_type: entityType,
          entity_id: entityId,
        };
      }
      
      await api.post('/communications', payload);
      onSuccess();
      
    } catch (err) {
      console.error('Failed to send:', err);
      setError('Failed to send communication');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" data-testid="compose-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">New Communication</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Type</label>
            <div className="flex flex-wrap gap-2">
              {['message', 'task', 'announcement'].map((t) => {
                const config = TYPE_CONFIG[t];
                const Icon = config.icon;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      type === t
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Priority */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            >
              {Object.entries(PRIORITY_CONFIG).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          {/* Subject (for tasks/announcements) */}
          {(type === 'task' || type === 'announcement') && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject..."
              />
            </div>
          )}
          
          {/* Content */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Type your message..."
              rows={4}
              data-testid="compose-content"
            />
          </div>
          
          {/* Context info */}
          {entityType && entityId && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <FileText className="w-4 h-4" />
                <span>This will be linked to: <strong className="capitalize">{entityType}</strong></span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!content.trim() || sending}
              className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
              data-testid="send-btn"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
